import React from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import { paymentRailLabel } from "../Platform/XprobaseConsole/passengerTaxConstants";
import { resolveMpFeeFromVenda } from "./passagemMpFeeUi";

const MP_PAYMENT_TYPE_LABELS = {
  pix: "PIX",
  bank_transfer: "PIX",
  credit_card: "Cartão de crédito",
  debit_card: "Cartão de débito",
  account_money: "Saldo XPay",
  boleto: "Boleto",
  ticket: "Boleto",
  atm: "Caixa eletrônico",
  prepaid_card: "Cartão pré-pago",
  mp_pending: "XPay (aguardando confirmação)",
  digital_currency: "Crédito digital",
  unknown: "Não informado",
  xpay: "XPay",
  mercadopago: "Link de pagamento",
  cielo: "Cielo",
  rede: "Rede",
  pix_itau: "PIX Itaú",
  appmax: "Appmax",
  other: "Outros",
};

export function normalizeMpPaymentTypeKey(typeId, methodId) {
  const type = String(typeId || "").trim().toLowerCase();
  const method = String(methodId || "").trim().toLowerCase();
  if (type === "bank_transfer" || method === "pix") return "pix";
  if (type === "account_money" || method === "account_money") return "account_money";
  if (type === "credit_card") return "credit_card";
  if (type === "debit_card") return "debit_card";
  if (type === "ticket") return "boleto";
  if (type) return type;
  if (method === "pix") return "pix";
  return method || "unknown";
}

export function mpPaymentTypeLabel(keyOrType, methodId, t) {
  const key =
    methodId !== undefined
      ? normalizeMpPaymentTypeKey(keyOrType, methodId)
      : String(keyOrType || "unknown");
  if (t) {
    return t(`passagensPage.mpPaymentType_${key}`, {
      defaultValue: MP_PAYMENT_TYPE_LABELS[key] || key,
    });
  }
  return MP_PAYMENT_TYPE_LABELS[key] || key;
}

export function resolveMpPaymentFromRow(row) {
  if (!row) return null;
  if (row.mp_payment_label) {
    return {
      label: row.mp_payment_label,
      installments: row.mp_installments ?? null,
      group: row.mp_payment_group || null,
    };
  }
  const mpFee = resolveMpFeeFromVenda(row);
  const typeId = row.mp_payment_type_id || mpFee?.payment_type_id || null;
  const methodId = row.mp_payment_method_id || mpFee?.payment_method_id || null;
  const installments = row.mp_installments ?? mpFee?.installments ?? null;
  const group = normalizeMpPaymentTypeKey(typeId, methodId);
  let label = null;
  if (typeId || methodId) {
    label = mpPaymentTypeLabel(typeId, methodId);
  } else if (row.forma_pagamento_nome) {
    label = row.forma_pagamento_nome;
  } else if (row.payment_rail) {
    label = paymentRailLabel(row.payment_rail);
  }
  return { label, installments, group, typeId, methodId };
}

export function buildMercadoPagoPaymentActivityUrl(mpPaymentId) {
  const raw = mpPaymentId != null ? String(mpPaymentId).trim() : "";
  if (!raw || !/^\d+$/.test(raw)) return null;
  return `https://www.mercadopago.com.br/activities/detail/payment-${raw}`;
}

export function MpPaymentIdCell({ value, title = "Abrir transação XPay" }) {
  const raw = value != null ? String(value).trim() : "";
  if (!raw) return "—";

  const href = buildMercadoPagoPaymentActivityUrl(raw);
  const textSx = {
    fontFamily: "monospace",
    fontSize: "0.78rem",
    maxWidth: 108,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  if (!href) {
    return (
      <Tooltip title={raw}>
        <Typography variant="body2" sx={textSx}>
          {raw}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={title ? `${title} · ${raw}` : raw}>
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        color="primary"
        sx={{
          ...textSx,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.35,
          fontWeight: 600,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {raw}
        <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }} />
      </Link>
    </Tooltip>
  );
}

export function formatInstallmentsLabel(installments, t) {
  const n = Number(installments);
  if (!Number.isFinite(n) || n <= 1) return null;
  return t("passagensPage.mpInstallmentsShort", { count: n, defaultValue: `${n}x` });
}

/** Rótulo de parcelas para painéis de detalhe (inclui à vista no cartão). */
export function formatInstallmentsBreakdownLabel(installments, t) {
  const n = Number(installments);
  if (!Number.isFinite(n) || n < 1) return null;
  if (n === 1) {
    return t("passagensPage.mpInstallmentsAtSight", { defaultValue: "À vista" });
  }
  return t("passagensPage.mpInstallmentsShort", { count: n, defaultValue: `${n}x` });
}

export default function PassengerPaymentCell({ row, t, showRailFallback = true }) {
  const mp = resolveMpPaymentFromRow(row);
  const primary =
    mp?.label ||
    (showRailFallback ? row?.forma_pagamento_nome || paymentRailLabel(row?.payment_rail) : null) ||
    "—";
  const instLabel = formatInstallmentsLabel(mp?.installments, t);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography variant="body2" component="span">
        {primary}
        {instLabel ? (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            · {instLabel}
          </Typography>
        ) : null}
      </Typography>
      {row?.intermediated ? <Chip size="small" label="XPay" variant="outlined" /> : null}
    </Stack>
  );
}

export function breakdownEntries(byMap = {}, labelFn) {
  return Object.entries(byMap || {})
    .filter(([, row]) => Number(row?.count || 0) > 0)
    .map(([key, row]) => ({
      key,
      label: labelFn(key),
      count: Number(row.count || 0),
      gmv: Number(row.gmv || 0),
    }))
    .sort((a, b) => b.count - a.count || b.gmv - a.gmv);
}
